import { useState } from 'react'
import { useTranslate } from '@ur-solutions/react-hooks'

import { cn } from '@/lib/utils'
import { FAIcon } from '@/components/fa-icon/fa-icon'

import { parseExportLogMetadata } from '../export-log-metadata'

const CHIP_CLASS =
  'flex w-fit items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-medium text-violet-700'

/**
 * Inline summary chip for an EXPORTED action, e.g. "PDF export · sent to 4
 * recipients". When the export was mailed, the chip is clickable to reveal
 * the recipient list. The format reflects what was actually requested.
 */
export const ExportChip = ({ metadata }: { metadata?: string | null }) => {
  const [open, setOpen] = useState(false)
  const t = useTranslate({
    formatChip: ['issues.action-log.export-format', { format: 'format' }],
    recipientsChip: ['issues.action-log.export-recipients', { count: 'count' }],
  })

  const meta = parseExportLogMetadata(metadata)
  if (!meta) return null

  const recipients = [...new Set(meta.recipients ?? [])]
  const parts: string[] = []
  if (meta.export_format) {
    const format = meta.export_format.replace(/_/g, ' ').toUpperCase()
    parts.push(t.formatChip({ format }))
  }
  if (recipients.length > 0) {
    parts.push(t.recipientsChip({ count: String(recipients.length) }))
  }
  if (parts.length === 0) return null

  const label = parts.join(' · ')

  if (recipients.length === 0) {
    return (
      <div className={CHIP_CLASS}>
        <FAIcon icon="circle-info" className="text-xs" />
        {label}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={cn(CHIP_CLASS, 'hover:brightness-95')}
      >
        <FAIcon icon="circle-info" className="text-xs" />
        {label}
        <FAIcon icon={open ? 'chevron-up' : 'chevron-down'} />
      </button>
      {open && (
        <div className="flex w-fit flex-col gap-1 rounded-md border border-violet-200 bg-violet-50 px-3 py-2">
          {recipients.map(email => (
            <span key={email} className="text-xs text-violet-700">
              {email}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
